import { MessageSquare, Copy, Check, ExternalLink, TestTube, User as UserIcon } from 'lucide-react';
import { useState } from 'react';
import ProfileManagement from './ProfileManagement';

interface SettingsProps {
  showBotSetup?: boolean;
  userId?: string;
}

export default function Settings({ showBotSetup = true, userId }: SettingsProps) {
  const [copied, setCopied] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'bot' | 'profile'>('profile');
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const webhookUrl = `${supabaseUrl}/functions/v1/telegram-webhook`;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const testOpenAIApiKey = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/parse-receipt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ test: true }),
      });

      const text = await response.text();

      if (response.ok) {
        setTestResult({ success: true, message: 'OpenAI API key is valid and working!' });
      } else {
        setTestResult({ success: false, message: `Error: ${text}` });
      }
    } catch (error) {
      setTestResult({ success: false, message: `Connection error: ${error.message}` });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {showBotSetup && (
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'profile'
                ? 'bg-brand-primary text-white shadow-brand'
                : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
            }`}
          >
            <UserIcon className="w-4 h-4" />
            Profile Settings
          </button>
          <button
            onClick={() => setActiveTab('bot')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'bot'
                ? 'bg-brand-primary text-white shadow-brand'
                : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Bot Setup
          </button>
        </div>
      )}

      {(!showBotSetup || activeTab === 'profile') && (
        userId ? (
          <ProfileManagement userId={userId} />
        ) : (
          <div className="card text-center py-12">
            <UserIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No User Profile</h3>
            <p className="text-gray-600 dark:text-gray-400">
              Please log in to view and edit your profile.
            </p>
          </div>
        )
      )}

      {showBotSetup && activeTab === 'bot' && (
        <>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-cyan-600 p-3 rounded-lg">
            <MessageSquare className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Telegram Bot Setup</h2>
            <p className="text-slate-600">Connect your Telegram bot to start managing receipts</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center space-x-2">
              <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
                1
              </span>
              <span>Create a Telegram Bot</span>
            </h3>
            <ol className="space-y-3 ml-8 text-slate-700">
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>
                  Open Telegram and search for{' '}
                  <a
                    href="https://t.me/BotFather"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-medium inline-flex items-center"
                  >
                    @BotFather
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>
                  Send the command <code className="bg-white px-2 py-1 rounded">/newbot</code>
                </span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Follow the instructions to choose a name and username for your bot</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>
                  Copy the bot token (it looks like{' '}
                  <code className="bg-white px-2 py-1 rounded text-xs">
                    123456789:ABCdefGHIjklMNOpqrsTUVwxyz
                  </code>
                  )
                </span>
              </li>
            </ol>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center space-x-2">
              <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
                2
              </span>
              <span>Configure Environment Variables</span>
            </h3>
            <div className="ml-8 space-y-4">
              <p className="text-slate-700">
                Add these environment variables in your Supabase project:
              </p>
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <code className="text-sm font-mono text-slate-800">TELEGRAM_BOT_TOKEN</code>
                  <button
                    onClick={() => copyToClipboard('TELEGRAM_BOT_TOKEN', 'token-key')}
                    className="p-1 hover:bg-slate-100 rounded transition-colors"
                  >
                    {copied === 'token-key' ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-slate-600" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-slate-600">
                  Your Telegram bot token from @BotFather
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <code className="text-sm font-mono text-slate-800">OPENAI_API_KEY</code>
                  <button
                    onClick={() => copyToClipboard('OPENAI_API_KEY', 'openai-key')}
                    className="p-1 hover:bg-slate-100 rounded transition-colors"
                  >
                    {copied === 'openai-key' ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-slate-600" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-slate-600 mb-3">
                  Your OpenAI API key for receipt parsing (get it from{' '}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-600 hover:underline"
                  >
                    OpenAI Platform
                  </a>
                  )
                </p>
                <button
                  onClick={testOpenAIApiKey}
                  disabled={testing}
                  className="w-full flex items-center justify-center space-x-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                >
                  <TestTube className="w-4 h-4" />
                  <span>{testing ? 'Testing...' : 'Test API Key'}</span>
                </button>
                {testResult && (
                  <div
                    className={`mt-3 p-3 rounded-lg text-sm ${
                      testResult.success
                        ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                        : 'bg-red-50 border border-red-200 text-red-800'
                    }`}
                  >
                    {testResult.message}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded p-3">
                💡 Go to Supabase Dashboard → Project Settings → Edge Functions → Add secret
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center space-x-2">
              <span className="bg-violet-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
                3
              </span>
              <span>Set Webhook URL</span>
            </h3>
            <div className="ml-8 space-y-4">
              <p className="text-slate-700">
                Set the webhook URL for your bot using this command in your browser or via curl:
              </p>
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-slate-600 uppercase">Webhook URL</p>
                  <button
                    onClick={() => copyToClipboard(webhookUrl, 'webhook')}
                    className="p-1 hover:bg-slate-100 rounded transition-colors"
                  >
                    {copied === 'webhook' ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-slate-600" />
                    )}
                  </button>
                </div>
                <code className="text-sm font-mono text-slate-800 break-all block">
                  {webhookUrl}
                </code>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-slate-600 uppercase">Set Webhook Command</p>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        `https://api.telegram.org/bot<YOUR_BOT_TOKEN>/setWebhook?url=${webhookUrl}`,
                        'command'
                      )
                    }
                    className="p-1 hover:bg-slate-100 rounded transition-colors"
                  >
                    {copied === 'command' ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-slate-600" />
                    )}
                  </button>
                </div>
                <code className="text-sm font-mono text-slate-800 break-all block">
                  https://api.telegram.org/bot&lt;YOUR_BOT_TOKEN&gt;/setWebhook?url={webhookUrl}
                </code>
              </div>
              <p className="text-sm text-slate-600">
                Replace <code className="bg-white px-2 py-1 rounded">&lt;YOUR_BOT_TOKEN&gt;</code>{' '}
                with your actual bot token and visit the URL in your browser.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center space-x-2">
              <span className="bg-amber-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">
                4
              </span>
              <span>Start Using Your Bot</span>
            </h3>
            <div className="ml-8 space-y-3 text-slate-700">
              <p>Once everything is set up:</p>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <span className="text-amber-600 font-bold">•</span>
                  <span>
                    Open your bot in Telegram and send{' '}
                    <code className="bg-white px-2 py-1 rounded">/start</code>
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-600 font-bold">•</span>
                  <span>Send photos or documents of your receipts</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-600 font-bold">•</span>
                  <span>
                    Use commands like <code className="bg-white px-2 py-1 rounded">/summary</code> to
                    get expense reports
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-600 font-bold">•</span>
                  <span>View and manage all receipts in this dashboard</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4">Available Bot Commands</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-slate-50 rounded-lg">
            <code className="text-emerald-600 font-semibold">/start</code>
            <p className="text-sm text-slate-600 mt-1">Initialize the bot and see welcome message</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <code className="text-emerald-600 font-semibold">/summary</code>
            <p className="text-sm text-slate-600 mt-1">Get your monthly expense summary</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <code className="text-emerald-600 font-semibold">/monthly</code>
            <p className="text-sm text-slate-600 mt-1">Get this month's expense report</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <code className="text-emerald-600 font-semibold">/yearly</code>
            <p className="text-sm text-slate-600 mt-1">Get this year's expense report</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-lg">
            <code className="text-emerald-600 font-semibold">/categories</code>
            <p className="text-sm text-slate-600 mt-1">List all expense categories</p>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
}
